import type { CSSProperties } from 'react'

import * as stylex from '@stylexjs/stylex'

import { parseAvatarConfig } from '../lib/avatar'
import AvatarArtwork from './AvatarArtwork'
import Icon, { type IconName } from './Icon'

type AvatarSubject = {
  name: string
  color?: number
  avatar?: string
}

type AvatarProps = {
  person: AvatarSubject
  size?: number
  className?: string
  style?: CSSProperties
}

const styles = stylex.create({
  root: {
    display: 'inline-flex',
    flexShrink: 0,
    alignItems: 'center',
    justifyContent: 'center',
    overflow: 'hidden',
    borderRadius: '9999px',
    backgroundColor: '#e8efe6',
    fontSize: '0.76em',
    fontWeight: 600,
    color: '#365846',
    boxShadow: '0 0 0 1px rgb(0 0 0 / 5%)',
  },
  fullSize: {
    width: '100%',
    height: '100%',
  },
  screenReaderOnly: {
    position: 'absolute',
    width: '1px',
    height: '1px',
    padding: 0,
    margin: '-1px',
    overflow: 'hidden',
    clip: 'rect(0, 0, 0, 0)',
    whiteSpace: 'nowrap',
    border: 0,
  },
})

const avatarIconNames = new Set<IconName>([
  'people',
  'food',
  'travel',
  'shopping',
  'gift',
  'receipt',
])

export default function Avatar({ person, size = 40, className = '', style }: AvatarProps) {
  const avatarImage = person.avatar?.startsWith('data:image/') || person.avatar?.startsWith('https://')
  const avatarIcon = person.avatar && avatarIconNames.has(person.avatar as IconName)
  const config = parseAvatarConfig(person.avatar, person.name)
  const initials = person.name
    .split(/\s+/)
    .map((part) => part.charAt(0))
    .join('')
    .slice(0, 2)

  const rootProps = stylex.props(styles.root)

  return (
    <span
      aria-label={person.name}
      className={`${rootProps.className} ${className}`.trim()}
      style={{ height: size, width: size, ...style }}
      title={person.name}
    >
      {avatarImage ? (
        <img alt="" draggable="false" src={person.avatar} style={{ height: '100%', width: '100%', objectFit: 'cover' }} />
      ) : avatarIcon ? (
        <Icon
          className=""
          name={person.avatar as IconName}
          style={{ height: size * 0.48, width: size * 0.48 }}
        />
      ) : (
        <svg aria-hidden="true" {...stylex.props(styles.fullSize)} viewBox="0 0 48 48">
          <AvatarArtwork config={config} />
        </svg>
      )}
      <span {...stylex.props(styles.screenReaderOnly)}>{initials}</span>
    </span>
  )
}
